
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Mail, ArrowLeft, CheckCircle, Shield } from "lucide-react";
import { getPasswordResetRedirectUrl } from "@/utils/auth-config";

const PasswordReset = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const redirectUrl = getPasswordResetRedirectUrl();
      console.log("Password reset using redirect URL:", redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        if (error.message.includes("Email not confirmed")) {
          setError("Please confirm your email first before resetting your password");
        } else if (error.message.includes("User not found")) {
          setError("No account found with this email address");
        } else {
          setError(error.message);
        }
        return;
      }

      setIsEmailSent(true);
      toast({
        title: "Reset email sent",
        description: "Check your inbox for password reset instructions",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="container flex items-center justify-center min-h-screen py-8">
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardHeader className="space-y-1 text-center bg-gradient-to-br from-blue-50 to-teal-50 rounded-t-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-[#0F4C81] to-teal-600 bg-clip-text text-transparent">
              Check your email
            </CardTitle>
            <CardDescription className="text-slate-600">
              We've sent password reset instructions to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-1">Security Notice</p>
                  <p className="text-sm text-blue-700">
                    Click the link in the email to reset your password. The link will expire in 1 hour for your security.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Button asChild variant="outline" className="w-full">
                <Link to="/auth">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sign In
                </Link>
              </Button>
              <Button 
                variant="ghost" 
                className="w-full" 
                onClick={() => {
                  setIsEmailSent(false);
                  setEmail("");
                }}
              >
                Try different email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container flex items-center justify-center min-h-screen py-8">
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="space-y-1 text-center bg-gradient-to-br from-blue-50 to-teal-50 rounded-t-lg">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-xl font-bold bg-gradient-to-r from-[#0F4C81] to-teal-600 bg-clip-text text-transparent">
              SnapRefund
            </span>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">Reset your password</CardTitle>
          <CardDescription className="text-slate-600">
            Enter your email and we'll send you a secure reset link
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  className="pl-10 border-slate-200 focus:border-[#0F4C81] focus:ring-[#0F4C81]"
                  disabled={isLoading}
                />
              </div>
              {error && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <span className="text-xs">⚠️</span>
                  {error}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-[#0F4C81] to-teal-600 hover:from-[#0F4C81]/90 hover:to-teal-700 text-white font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send reset link
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button asChild variant="ghost" className="text-sm text-slate-600 hover:text-slate-800">
              <Link to="/auth">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordReset;
